/**
 * Thin fetch wrappers for the admin dashboard. Every call includes the
 * session cookie via `credentials: 'include'`. 401s are surfaced as
 * thrown errors so route guards can redirect to `/admin/login`.
 */

export type AdMode = 'off' | 'admob' | 'custom';

export interface AdsConfig {
  mode: AdMode;
  imageUrl: string | null;
  linkUrl: string | null;
  caption: string | null;
}

export class ApiError extends Error {
  constructor(public status: number, public body: unknown) {
    super(`HTTP ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(init.body && !(init.body instanceof FormData)
        ? { 'content-type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
    ...init,
  });
  const text = await res.text();
  const body = text ? safeJson(text) : null;
  if (!res.ok) throw new ApiError(res.status, body);
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function login(password: string): Promise<void> {
  await request('/v1/admin/login', {
    method: 'POST',
    body: JSON.stringify({ password }),
  });
}

export async function logout(): Promise<void> {
  await request('/v1/admin/logout', { method: 'POST' });
}

export async function getMe(): Promise<{ admin: true }> {
  return request('/v1/admin/me');
}

export async function getAds(): Promise<Partial<AdsConfig>> {
  return request('/v1/admin/config/ads');
}

export async function saveAds(payload: AdsConfig): Promise<void> {
  await request('/v1/admin/config/ads', {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export async function uploadImage(file: File): Promise<{ url: string }> {
  const form = new FormData();
  form.append('file', file);
  return request('/v1/admin/uploads/ads', {
    method: 'POST',
    body: form,
  });
}
